import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'

/** Narrowest the open panel may be, whatever the control's own width. */
const PANEL_MIN_WIDTH_PX = 280
/** Roughly the panel's full height, used only to decide whether to open upwards. */
const PANEL_MAX_HEIGHT_PX = 320
/** Gap between the control and the panel, and the least space kept to the viewport edge. */
const PANEL_GAP_PX = 4

export interface ComboboxPanel {
  open: boolean
  setOpen: (open: boolean) => void
  /** Wraps control and panel; a pointer press outside it closes the panel. */
  rootRef: React.RefObject<HTMLDivElement>
  triggerRef: React.RefObject<HTMLButtonElement>
  searchRef: React.RefObject<HTMLInputElement>
  /** Put on the active row so the keyboard cursor stays scrolled into view. */
  activeRowRef: React.RefObject<HTMLButtonElement>
  panelStyle: CSSProperties
  activeIndex: number
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  /**
   * Escape and the arrow keys, which behave the same in every panel.
   *
   * @returns true when the key was consumed, so the caller can handle only the
   *   keys whose meaning actually differs — Enter, mostly, which closes a
   *   single-choice panel and deliberately does not close a multi-choice one.
   */
  handleNavigationKey: (event: React.KeyboardEvent) => boolean
}

/**
 * The parts of a dropdown panel that have nothing to do with what is in it:
 * where it is drawn, when it closes, and how the arrow keys move.
 *
 * WHY `position: fixed`. These panels open inside the create-meeting modal,
 * which scrolls (`overflow-y-auto`), and CSS forces `overflow-x` to `auto` on a
 * box whose other axis is not `visible` — so an absolutely positioned panel is
 * *clipped* by the modal rather than merely overflowing it. That was BRAI-503.
 * Positioning against the viewport, clamping both edges and flipping upwards
 * when the space below is short is the fix, and it is the only reason this
 * lives in a hook instead of being written twice.
 *
 * @param optionCount How many rows the panel is currently showing, so the
 *   cursor cannot arrow past the end of a list that has just been filtered.
 */
export function useComboboxPanel(optionCount: number): ComboboxPanel {
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const activeRowRef = useRef<HTMLButtonElement>(null)

  const [open, setOpen] = useState(false)
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({})
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!open) return undefined
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (open) searchRef.current?.focus()
  }, [open])

  // Keeps the keyboard cursor visible while arrowing through the list.
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  // A filtered list can be shorter than where the cursor was left.
  useEffect(() => {
    setActiveIndex((current) => Math.min(current, Math.max(optionCount - 1, 0)))
  }, [optionCount])

  useLayoutEffect(() => {
    if (!open) return undefined

    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return

      const width = Math.max(rect.width, PANEL_MIN_WIDTH_PX)
      const spaceBelow = window.innerHeight - rect.bottom
      const openUpwards = spaceBelow < PANEL_MAX_HEIGHT_PX && rect.top > spaceBelow

      setPanelStyle({
        position: 'fixed',
        width,
        left: Math.max(
          PANEL_GAP_PX,
          Math.min(rect.left, window.innerWidth - width - PANEL_GAP_PX)
        ),
        ...(openUpwards
          ? { bottom: window.innerHeight - rect.top + PANEL_GAP_PX }
          : { top: rect.bottom + PANEL_GAP_PX }),
        zIndex: 100,
      })
    }

    place()
    // Capture phase: the control scrolls inside the modal, not the page.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open])

  function handleNavigationKey(event: React.KeyboardEvent): boolean {
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      triggerRef.current?.focus()
      return true
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((current) => Math.min(current + 1, optionCount - 1))
      return true
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((current) => Math.max(current - 1, 0))
      return true
    }
    return false
  }

  return {
    open,
    setOpen,
    rootRef,
    triggerRef,
    searchRef,
    activeRowRef,
    panelStyle,
    activeIndex,
    setActiveIndex,
    handleNavigationKey,
  }
}
